import React, { useEffect } from "react";
import FormRenderer from "Common/Custom/FormRenderer";
import DeleteButton from "./DeleteButton";
import { formatValue } from "./utils/checkoutTableUtils";

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const AdvanceDetailsList = ({
  Advance_Details,
  paymentMethodTypesOption,
  values,
  setValues,
  setErrors,
  errors,
  emptyPaymentDetail,
  validateField,
  AdvanceDetailsRules,
  dueAmount = 0,
  creditAmount = 0
}) => {


  const rows = values?.PaymentDetail || [];
  const baseFields = Advance_Details?.filter((e) => e.name !== "remaining_amount");

  // ---------------------------------------------------------------------
  // Single source of truth for all money figures.
  // ---------------------------------------------------------------------
  const totalAdvance = round2(
    rows.reduce((sum, r) => sum + Number(r.advance_amount || 0), 0)
  );
  const due = round2(dueAmount);
  const openingCredit = round2(creditAmount);

  // remaining >= 0  -> still owed, show as "Remaining"
  // remaining <  0  -> paid past the due amount, show as "Balance" (credit)
  // Amount still due after current payment
  const remaining = Math.max(round2(due - totalAdvance), 0);

  // Extra paid in this checkout
  const extraPaid = Math.max(round2(totalAdvance - due), 0);

  // Final customer credit
  const creditBalance = round2(openingCredit + extraPaid);

  // Show Balance whenever customer has any credit
  const isCredit = creditBalance > 0;

  // progress = totalAdvance / due, clamped 0-100, guarded against due = 0
  const progressPercent = due > 0 ? Math.min(Math.max((totalAdvance / due) * 100, 0), 100) : 0;

  const statusBadge =
    remaining === 0 && creditBalance === 0
      ? { text: "Ready for Checkout", className: "bg-success" }
      : isCredit
        ? { text: "Extra Balance", className: "bg-info text-dark" }
        : { text: "Pending Payment", className: "bg-warning text-dark" };

  // "Remaining" row: warning while pending, success once settled.
  // "Balance" row (credit) gets its own info styling — it isn't an error.
  const bottomRowLabel = isCredit ? "Balance" : "Remaining";

  const bottomRowValue = isCredit
    ? creditBalance
    : remaining;

  const bottomRowTextClass = isCredit
    ? "text-info"
    : remaining === 0 && creditBalance === 0
      ? "text-success"
      : "text-warning";

  // ---------------------------------------------------------------------
  // No blocking validation here anymore: paying more than `due` is a
  // valid outcome (it becomes a credit balance carried forward), not an
  // error state. Field-level rules (required, format, etc.) still run in
  // handleRowChange below.
  // ---------------------------------------------------------------------

  // ---------------------------------------------------------------------
  // Auto-fill the first payment row's advance_amount with the due amount.
  // Only runs when that row's advance_amount is still empty, so it won't
  // overwrite a value the user already entered. Mirrors the
  // FillAutoAdvanceAmount pattern used for total_amount.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!dueAmount) return;

    setValues((prev) => {
      if (!prev.PaymentDetail?.length) return prev;

      const firstRow = prev.PaymentDetail[0];
      if (firstRow?.advance_amount) return prev;

      const PaymentDetail = [...prev.PaymentDetail];
      PaymentDetail[0] = {
        ...PaymentDetail[0],
        advance_amount: String(dueAmount || ""),
      };

      return {
        ...prev,
        PaymentDetail,
      };
    });
  }, [dueAmount]);

  const handleAdd = () => {
    setValues((prev) => ({
      ...prev,
      PaymentDetail: [
        ...prev.PaymentDetail,
        { ...emptyPaymentDetail, Unic_key: crypto.randomUUID() },
      ],
    }));
  };

  const handleRemove = (index) => {
    setValues((prev) => ({
      ...prev,
      PaymentDetail: prev.PaymentDetail.filter((_, i) => i !== index),
    }));
  };

  const handleRowChange = (index) => (eOrName, value) => {
    let fieldName;
    let fieldValue;

    if (typeof eOrName === "object" && eOrName?.target) {
      fieldName = eOrName.target.name;
      fieldValue = eOrName.target.value;
    } else {
      fieldName = eOrName;
      fieldValue = value;
    }

    setValues((prev) => {
      const updatedRows = [...prev.PaymentDetail];

      updatedRows[index] = {
        ...updatedRows[index],
        [fieldName]: fieldValue,
      };

      return {
        ...prev,
        PaymentDetail: updatedRows,
      };
    });

    // Standard field-level validation
    if (AdvanceDetailsRules[fieldName]) {
      const errorMsg = validateField(fieldName, fieldValue, AdvanceDetailsRules);

      setErrors((prev) => {
        const paymentErrors = [...(prev.PaymentDetail || [])];

        paymentErrors[index] = {
          ...(paymentErrors[index] || {}),
          [fieldName]: errorMsg,
        };

        return {
          ...prev,
          PaymentDetail: paymentErrors,
        };
      });
    }
  };



  return (
    <div className="col-12">
      <div className="row g-3 w-100">
        {/* ---------------------------------------------------------------
            SECTION HEADER
        --------------------------------------------------------------- */}
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 border-bottom">
            <h5 className="mb-0 fw-semibold">Advance Payment Details</h5>
            <button
              type="button"
              onClick={handleAdd}
              className="btn btn-primary btn-sm d-flex align-items-center gap-1"
              title="Add Payment Detail"
              aria-label="Add Payment Detail"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="svg-stroke"
              >
                <path stroke="none" d="M0 0h24v24H0z" />
                <path d="M12 5l0 14" />
                <path d="M5 12l14 0" />
              </svg>
              Add Payment
            </button>
          </div>
        </div>

        {/* ---------------------------------------------------------------
            LEFT COLUMN — Payment entry cards
        --------------------------------------------------------------- */}
        <div className="col-lg-8">
          <div className="d-flex flex-column gap-3">
            {rows.length === 0 && (
              <div className="card border-dashed shadow-sm">
                <div className="card-body text-center text-muted py-4">
                  No payment entries yet. Click "Add Payment" to begin.
                </div>
              </div>
            )}

            {rows.map((rowValues, index) => {
              const fields = baseFields?.map((field) => ({
                ...field,
              }));

              const rowErrors = errors?.PaymentDetail?.[index] || {};

              return (
                <div
                  className="card  rounded-3 border-1"
                  key={rowValues.Unic_key || index}
                >
                  <div className="card-header bg-light d-flex justify-content-between align-items-center rounded-top-3">
                    <span className="fw-semibold">
                      Payment #{index + 1}
                    </span>

                    {rows.length > 1 && (
                      <DeleteButton disabled={false} onClick={() => handleRemove(index)} />
                    )}
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      <FormRenderer
                        fields={fields}
                        values={rowValues}
                        errors={rowErrors}
                        handleChange={handleRowChange(index)}
                        optionConfig={{
                          payment_method: {
                            list: paymentMethodTypesOption,
                            valueKey: "payment_method_id",
                            labelKey: "payment_method_name",
                          },
                        }}
                        isrow={false}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------------------------------------------------------------
            RIGHT COLUMN — Sticky Payment Summary
        --------------------------------------------------------------- */}
        <div className="col-lg-4">
          <div className="card rounded-3 border-1 sticky-top" style={{ top: "1rem" }}>
            <div className="card-header bg-light rounded-top-3">
              <span className="fw-semibold">Payment Summary</span>
            </div>

            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-muted">Due Amount</span>
                <span className="fw-bold text-primary fs-5">
                  {formatValue(due, "currency")}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-muted">Total Paid</span>
                <span className="fw-bold text-success fs-5">
                  {formatValue(totalAdvance, "currency")}
                </span>
              </div>

              {/* Sign-aware row: "Remaining" while due > paid,
                  "Balance" (credit) once paid exceeds due. Always shown
                  as a positive number — the label carries the meaning. */}
              <div className="d-flex justify-content-between align-items-center py-2 mb-3">
                <span className="text-muted">{bottomRowLabel}</span>
                <span className={`fw-bold fs-5 ${bottomRowTextClass}`}>
                  {formatValue(bottomRowValue, "currency")}
                </span>
              </div>

              {/* Progress bar: totalAdvance / due, capped at 100% — any
                  amount past 100% is the credit balance shown above. */}
              <div className="progress mb-3" style={{ height: "10px" }}>
                <div
                  className={`progress-bar ${remaining === 0
                    ? "bg-success"
                    : isCredit
                      ? "bg-info"
                      : "bg-warning"
                    }`}
                  role="progressbar"
                  style={{ width: `${progressPercent}%` }}
                  aria-valuenow={progressPercent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>

              <div className="text-center mb-3">
                <span className={`badge ${statusBadge.className} px-3 py-2`}>
                  {statusBadge.text}
                </span>
              </div>

              {/* Informational note for a credit balance — not an error,
                  just tells the person what will be carried forward. */}
              {isCredit && (
                <div className="alert alert-info py-2 px-3 mb-0 small border border-info">
                  Paid {formatValue(creditBalance, "currency")} more than the due amount.
                  This will be recorded as a credit balance.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvanceDetailsList;